import { selectAuth } from '@brands/store/selectors/auth';
import { AuthState } from '@brands/store/slices/authSlice';
import { Client } from '@twilio/conversations';
import React, { createContext, FC, ReactNode, useCallback, useEffect, useState } from 'react';

import { getConversationToken } from '../../services/communication/getConversationToken';
import { useJwtExpired } from '../useJwtExpired';
import { useAppSelector } from '../useReduxHook';

/**
 * Context holding the app-wide Twilio Conversations Client singleton.
 * Consumed via `useTwilioConversationsClient`.
 *
 * Prior to DO-3975, `useTwilioConversationsClient` held the `Client`
 * in component-local `useState`, which meant every consumer site
 * instantiated its own `Client` — and therefore opened its own
 * WebSocket — for the same logged-in user. Hoisting the state into
 * a single Provider at the App root collapses every consumer down
 * to one shared Client.
 *
 * Mirrors the dentistry-one brand's Provider byte-for-byte except for
 * the `getConversationToken()` call signature — the default brand's
 * version of that service doesn't take a `userRole` argument.
 */
export const TwilioConversationsClientContext = createContext<Client | null>(null);

interface TwilioConversationsClientProviderProps {
  children: ReactNode;
}

export const TwilioConversationsClientProvider: FC<TwilioConversationsClientProviderProps> = ({ children }) => {
  const userData: AuthState = useAppSelector(selectAuth);
  const [token, setToken] = useState<string | null>(null);
  const [client, setClient] = useState<Client | null>(null);

  const renewToken = useCallback(async (): Promise<void> => {
    const newToken = await getConversationToken();
    setToken(newToken);
  }, []);

  async function renewClient(): Promise<void> {
    if (client) {
      if (token) {
        await client.updateToken(token);
      } else {
        await client.shutdown();
      }
    } else if (token) {
      setClient(new Client(token));
    } else {
      setClient(null);
    }
  }

  useEffect(() => {
    if (userData.userInfo) {
      renewToken();
    } else {
      setToken(null);
    }
  }, [userData]);

  useEffect(() => {
    renewClient();
  }, [token]);

  useJwtExpired(renewToken, token);

  return (
    <TwilioConversationsClientContext.Provider value={client}>{children}</TwilioConversationsClientContext.Provider>
  );
};

export default TwilioConversationsClientProvider;
